import { Skeleton } from "@/components/kit";
import { formatLak } from "@/shared/lib/currency";
import type { ShippingPerformanceResultDTO } from "../../domain/types";
import { ReportMobileCard, ReportStat } from "./ReportProductLink";

type ShippingPerformanceTableProps = {
  result?: ShippingPerformanceResultDTO;
  isLoading?: boolean;
  embedded?: boolean;
};

function ShippingMobileCard({
  row,
}: {
  row: ShippingPerformanceResultDTO["data"][number];
}) {
  const rateNum = Number(row.returnRatePercent);
  const rateClass =
    rateNum >= 15
      ? "text-destructive font-medium"
      : rateNum >= 8
        ? "text-amber-700 dark:text-amber-400"
        : "";

  return (
    <ReportMobileCard>
      <div className="flex flex-col gap-0.5">
        <span className="font-medium">{row.shippingCarrier}</span>
        <span className="text-muted-foreground text-xs">
          {row.shippingBranch ?? "— ບໍ່ລະບຸສາຂາ —"}
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <ReportStat label="ຈັດສົ່ງ" value={row.shippedCount} />
        <ReportStat label="ຕີກັບ" value={row.returnedCount} />
        <ReportStat
          label="ອັດຕາຕີກັບ"
          value={`${row.returnRatePercent}%`}
          valueClassName={rateClass}
        />
        <ReportStat
          label="ຄ່າສົ່ງເສຍ"
          value={formatLak(row.totalReturnShippingLoss)}
        />
        <ReportStat
          label="ເສຍ/ຄັ້ງ"
          value={row.avgLossPerReturn ? formatLak(row.avgLossPerReturn) : "—"}
          className="col-span-2"
        />
      </dl>
      <p className="mt-3 border-secondary/10 border-t pt-3 text-muted-foreground text-xs leading-snug">
        {row.insight}
      </p>
    </ReportMobileCard>
  );
}

export function ShippingPerformanceTable({
  result,
  isLoading,
}: ShippingPerformanceTableProps) {
  const rows = result?.data ?? [];

  if (isLoading) {
    return <Skeleton className="h-72 w-full rounded-2xl" />;
  }

  return (
    <section className="brand-card overflow-hidden">
      <div className="flex flex-col gap-1 p-4 sm:p-5">
        <h2 className="font-display text-lg">ຂົນສົ່ງ / ຕີກັບ</h2>
        <p className="text-muted-foreground text-sm">
          ອັດຕາຕີກັບ COD ແຍກຕາມຂົນສົ່ງ ແລະ ສາຂາ
        </p>
        {result?.meta ? (
          <p className="text-muted-foreground text-xs">
            ຈັດສົ່ງ {result.meta.totalShipped} · ຕີກັບ {result.meta.totalReturned} ·
            ຄ່າສົ່ງເສຍ {formatLak(result.meta.totalLoss)}
          </p>
        ) : null}
      </div>

      {rows.length === 0 ? (
        <div className="border-secondary/15 border-t p-8 text-center text-muted-foreground text-sm">
          ບໍ່ມີຂໍ້ມູນຂົນສົ່ງໃນຊ່ວງນີ້
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3 border-secondary/15 border-t p-4 md:hidden">
            {rows.map((row) => {
              const key = `${row.shippingCarrier}:${row.shippingBranch ?? ""}`;
              return <ShippingMobileCard key={key} row={row} />;
            })}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40 text-muted-foreground text-xs">
                <tr>
                  <th className="px-4 py-3 font-medium">ຂົນສົ່ງ / ສາຂາ</th>
                  <th className="px-4 py-3 text-right font-medium">ຈັດສົ່ງ</th>
                  <th className="px-4 py-3 text-right font-medium">ຕີກັບ</th>
                  <th className="px-4 py-3 text-right font-medium">ອັດຕາຕີກັບ</th>
                  <th className="px-4 py-3 text-right font-medium">ຄ່າສົ່ງເສຍ</th>
                  <th className="px-4 py-3 text-right font-medium">ເສຍ/ຄັ້ງ</th>
                  <th className="px-4 py-3 font-medium">ແນະນຳ</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const key = `${row.shippingCarrier}:${row.shippingBranch ?? ""}`;
                  const rateNum = Number(row.returnRatePercent);
                  const rateClass =
                    rateNum >= 15
                      ? "text-destructive font-medium"
                      : rateNum >= 8
                        ? "text-amber-700 dark:text-amber-400"
                        : "";

                  return (
                    <tr key={key} className="border-secondary/10 border-t">
                      <td className="px-4 py-3">
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {row.shippingCarrier}
                          </span>
                          <span className="text-muted-foreground text-xs">
                            {row.shippingBranch ?? "— ບໍ່ລະບຸສາຂາ —"}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.shippedCount}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.returnedCount}
                      </td>
                      <td
                        className={`px-4 py-3 text-right tabular-nums ${rateClass}`}
                      >
                        {row.returnRatePercent}%
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {formatLak(row.totalReturnShippingLoss)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        {row.avgLossPerReturn
                          ? formatLak(row.avgLossPerReturn)
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">
                        {row.insight}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
